import type { GetListParams, GetListResult } from "ra-core";

import { getSupabaseClient } from "../supabase/supabase";

/**
 * "Like & Save" for echoes.
 *
 * Saves live in the CRM's own Supabase table `echo_saves`, one row per
 * (signed-in user, echo id), scoped by RLS so a plain read of the table is
 * always "my saves". Echo rows themselves come from the dashboard API (or the
 * Supabase view), which knows nothing about saves — so the list's "Saved
 * echoes" toggle is resolved here, before the echo query is issued, into an
 * `id@in` filter over the user's saved ids. Both echo providers call this.
 */

export const SAVED_ECHOES_RESOURCE = "echo_saves";

/** The list filter key the "Saved echoes" toggle writes. */
export const SAVED_ECHOES_FILTER = "saved";

export type ResolvedListParams<P> = {
  params: P;
  /** True when the user has no saves: the list is empty without a query. */
  empty: boolean;
};

// `any` on purpose: ra-core's DataProvider.getList is generic in the record
// type, so a concretely typed empty result would narrow the provider's
// signature and break `useDataProvider<CrmDataProvider>()` call sites.
export const EMPTY_LIST_RESULT: GetListResult<any> = {
  data: [],
  total: 0,
};

export function isSavedEchoesFilterOn(
  filter: Record<string, unknown> | undefined,
): boolean {
  const value = filter?.[SAVED_ECHOES_FILTER];
  return value === true || value === "true";
}

function withoutSavedKey(
  filter: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const rest = { ...(filter ?? {}) };
  delete rest[SAVED_ECHOES_FILTER];
  return rest;
}

/**
 * Pure: replace the `saved` toggle with `id@in` over the given saved ids.
 *
 * The value takes PostgREST's list form, `(a,b,c)`: that is what the Supabase
 * provider and the demo's filter adapter expect, the dashboard API strips
 * the parentheses, and the AWS client-side matcher reads both forms.
 */
export function withSavedEchoIds<P extends Partial<GetListParams>>(
  params: P,
  savedIds: readonly string[],
): ResolvedListParams<P> {
  const filter = withoutSavedKey(params.filter);
  if (savedIds.length === 0) {
    return { params: { ...params, filter }, empty: true };
  }
  return {
    params: {
      ...params,
      filter: { ...filter, "id@in": `(${savedIds.join(",")})` },
    },
    empty: false,
  };
}

/** The signed-in user's saved echo ids; RLS scopes the table to the caller. */
export async function listSavedEchoIds(): Promise<string[]> {
  const { data, error } = await getSupabaseClient()
    .from(SAVED_ECHOES_RESOURCE)
    .select("echo_id");
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => String(row.echo_id));
}

/**
 * Resolve a list request's `saved` toggle. Untouched when the toggle is
 * absent; the key is dropped when it is off; when on, the user's saved ids
 * become an `id@in` filter (or `empty` when there is nothing to show).
 */
export async function resolveSavedEchoesFilter<
  P extends Partial<GetListParams>,
>(
  params: P,
  listIds: () => Promise<string[]> = listSavedEchoIds,
): Promise<ResolvedListParams<P>> {
  const filter = params.filter as Record<string, unknown> | undefined;
  if (!filter || !(SAVED_ECHOES_FILTER in filter)) {
    return { params, empty: false };
  }
  if (!isSavedEchoesFilterOn(filter)) {
    return {
      params: { ...params, filter: withoutSavedKey(filter) },
      empty: false,
    };
  }
  return withSavedEchoIds(params, await listIds());
}
